/**
 * <store-carousel> — pagination dots and mouse drag for a CSS scroll-snap track.
 *
 * The track scrolls and snaps with CSS alone, so if this script never loads the
 * carousel still works by swipe/scroll; only the dots and mouse-dragging are
 * missing. Dots are generated here rather than in Liquid because the number of
 * pages depends on how many cards fit at the current viewport width.
 *
 * Dragging is mouse-only on purpose. Touch already swipes this track natively,
 * with momentum and snapping the browser does better than JS can.
 */
(function () {
  'use strict';

  if (customElements.get('store-carousel')) return;

  /** Movement (px) below which a pointer gesture is still treated as a click. */
  const DRAG_THRESHOLD = 4;
  /** Release speed (px/ms) above which a short drag still advances a card. */
  const FLICK_VELOCITY = 0.35;
  /** How long the settle animation is given before snapping is re-enabled. */
  const SETTLE_MS = 420;

  class StoreCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.store-carousel__track');
      this.dotsEl = this.querySelector('[data-carousel-dots]');

      if (!this.track) return;

      this.endDrag = this.endDrag.bind(this);
      this.onResize = this.throttle(this.build.bind(this));

      this.build();
      window.addEventListener('resize', this.onResize, { passive: true });

      if (this.dotsEl) {
        this.onScroll = this.throttle(this.setActiveDot.bind(this));
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
      }

      this.initDrag();
    }

    disconnectedCallback() {
      if (!this.track) return;

      window.clearTimeout(this.snapTimer);
      window.removeEventListener('resize', this.onResize);
      if (this.onScroll) this.track.removeEventListener('scroll', this.onScroll);

      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
      this.track.removeEventListener('click', this.onClickCapture, true);
    }

    /** rAF throttle — keeps scroll/resize handlers off the critical path (INP). */
    throttle(fn) {
      let ticking = false;
      return function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          fn();
        });
      };
    }

    /**
     * Cache where each card starts, how many fit, and how far the track goes.
     *
     * Read once per build rather than per dot per scroll frame: setActiveDot
     * runs on every scroll, and laying out to measure there would cost INP.
     */
    measure() {
      const items = Array.prototype.slice.call(this.track.children);
      const trackLeft = this.track.getBoundingClientRect().left;
      const scrolled = this.track.scrollLeft;

      this.cardOffsets = items.map(function (item) {
        return Math.round(item.getBoundingClientRect().left - trackLeft + scrolled);
      });

      // Stride is card + gap, so it can't be read off a single card's width.
      const stride = this.cardOffsets.length > 1
        ? this.cardOffsets[1] - this.cardOffsets[0]
        : this.track.clientWidth;

      this.cardsPerView = stride > 0
        ? Math.max(1, Math.round(this.track.clientWidth / stride))
        : 1;

      this.maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    get pageCount() {
      const cards = this.cardOffsets ? this.cardOffsets.length : 0;
      if (cards === 0 || this.track.clientWidth === 0) return 1;
      return Math.max(1, Math.ceil(cards / this.cardsPerView));
    }

    /**
     * Left offset for page `i` — the start of the first card on that page.
     *
     * Has to land on a card edge. The track is `scroll-snap-type: x mandatory`,
     * so the browser re-snaps to the nearest card after any programmatic
     * scroll, and a target part-way through a card gets pulled to whichever
     * edge is closer. Paging by `clientWidth * i` put page 3 of a 6-card track
     * 40px short of the end, which snapped to the last page — the third dot
     * jumped to the final slide and was indistinguishable from the fourth.
     *
     * Still clamped to how far the track can actually scroll, so the position
     * the dots scroll to and the position they read back stay identical.
     */
    pageOffset(index) {
      if (!this.cardOffsets || this.cardOffsets.length === 0) return 0;

      const card = Math.min(index * this.cardsPerView, this.cardOffsets.length - 1);
      return Math.min(this.cardOffsets[card], this.maxScroll);
    }

    get scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    build() {
      this.measure();
      const pages = this.pageCount;

      // Only advertise the grab cursor when there is somewhere to drag to.
      this.track.classList.toggle('is-draggable', pages > 1);

      if (!this.dotsEl) return;

      if (pages <= 1) {
        this.dotsEl.hidden = true;
        this.dotsEl.textContent = '';
        return;
      }

      if (this.dotsEl.childElementCount === pages) {
        this.setActiveDot();
        return;
      }

      this.dotsEl.hidden = false;
      this.dotsEl.textContent = '';

      for (let i = 0; i < pages; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'store-carousel__dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1) + ' of ' + pages);
        dot.addEventListener('click', () => {
          this.track.scrollTo({
            left: this.pageOffset(i),
            behavior: this.scrollBehavior
          });
        });
        this.dotsEl.appendChild(dot);
      }

      this.setActiveDot();
    }

    setActiveDot() {
      if (!this.dotsEl) return;

      const dots = this.dotsEl.children;
      if (dots.length === 0) return;

      // Nearest page offset rather than scrollLeft / clientWidth: the offsets
      // are not evenly spaced once the final page is clamped to the track end.
      const scrolled = this.track.scrollLeft;
      let index = 0;
      let nearest = Infinity;

      for (let i = 0; i < dots.length; i += 1) {
        const offset = Math.abs(this.pageOffset(i) - scrolled);
        if (offset < nearest) {
          nearest = offset;
          index = i;
        }
      }

      for (let i = 0; i < dots.length; i += 1) {
        const active = i === index;
        dots[i].classList.toggle('is-active', active);
        dots[i].setAttribute('aria-current', active ? 'true' : 'false');
      }
    }

    initDrag() {
      let dragging = false;
      let pointerId = null;
      let startX = 0;
      let startScroll = 0;
      let distance = 0;
      let lastX = 0;
      let lastTime = 0;
      let velocity = 0;

      this.onPointerDown = (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (this.pageCount <= 1) return;

        window.clearTimeout(this.snapTimer);

        dragging = true;
        pointerId = event.pointerId;
        startX = event.clientX;
        lastX = event.clientX;
        lastTime = event.timeStamp;
        velocity = 0;
        distance = 0;
        startScroll = this.track.scrollLeft;
      };

      this.onPointerMove = (event) => {
        if (!dragging || event.pointerId !== pointerId) return;

        const dx = event.clientX - startX;
        distance = Math.max(distance, Math.abs(dx));

        // Take the pointer over only once this is clearly a drag, so a plain
        // click on a card is still a click.
        if (distance <= DRAG_THRESHOLD) return;

        if (!this.track.hasPointerCapture(pointerId)) {
          this.track.setPointerCapture(pointerId);
          this.track.classList.add('is-dragging');
        }

        const elapsed = event.timeStamp - lastTime;
        if (elapsed > 0) velocity = (event.clientX - lastX) / elapsed;
        lastX = event.clientX;
        lastTime = event.timeStamp;

        event.preventDefault();
        this.track.scrollLeft = startScroll - dx;
      };

      this.onPointerUp = (event) => {
        if (!dragging || event.pointerId !== pointerId) return;

        dragging = false;
        if (this.track.hasPointerCapture(pointerId)) {
          this.track.releasePointerCapture(pointerId);
        }
        pointerId = null;

        if (this.track.classList.contains('is-dragging')) this.settle(velocity);
      };

      // A drag that ends on a card must not also trigger links inside the quote.
      this.onClickCapture = (event) => {
        if (distance <= DRAG_THRESHOLD) return;
        distance = 0;
        event.preventDefault();
        event.stopPropagation();
      };

      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointermove', this.onPointerMove, { passive: false });
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
      this.track.addEventListener('click', this.onClickCapture, true);
    }

    /** Animate to the card the drag landed nearest, then hand snapping back to CSS. */
    settle(velocity) {
      const items = this.track.children;
      if (items.length === 0) {
        this.endDrag();
        return;
      }

      const trackLeft = this.track.getBoundingClientRect().left;
      let index = 0;
      let nearest = Infinity;

      for (let i = 0; i < items.length; i += 1) {
        const offset = Math.abs(items[i].getBoundingClientRect().left - trackLeft);
        if (offset < nearest) {
          nearest = offset;
          index = i;
        }
      }

      // A flick carries on to the neighbouring card even when the drag was short.
      if (Math.abs(velocity) > FLICK_VELOCITY) {
        index += velocity < 0 ? 1 : -1;
        index = Math.min(items.length - 1, Math.max(0, index));
      }

      const behavior = this.scrollBehavior;
      const left = this.track.scrollLeft + (items[index].getBoundingClientRect().left - trackLeft);

      this.track.scrollTo({ left: left, behavior: behavior });

      // Snapping stays off until the animation finishes — re-applying
      // `scroll-snap-type` mid-scroll yanks the track to its target instantly.
      this.snapTimer = window.setTimeout(this.endDrag, behavior === 'smooth' ? SETTLE_MS : 0);
    }

    endDrag() {
      this.track.classList.remove('is-dragging');
    }
  }

  customElements.define('store-carousel', StoreCarousel);
})();
